import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from '../core/app.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-file-an-issue',
  templateUrl: './file-an-issue.component.html',
  styleUrls: ['./file-an-issue.component.scss']
})
export class FileAnIssueComponent implements OnInit, OnDestroy {
  @ViewChild('errorSummary') errorSummary: ElementRef;

  private moverSearchSubject = new Subject<string>();
  private moverSearchSubscription: Subscription;

  // Stage 1
  email = '';
  emailConfirm = '';
  gotcha = '';
  currentCode = '';
  stage1Done = false;
  stage1Errors: any = {};
  codeChars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  codeArt: {ch: string, style: any}[] = [];
  showGotchaAlt = false;

  // Stage 2
  firstName = '';
  lastName = '';
  moverQuery = '';
  moverId = '';
  fromCountry = '';
  fromState = '';
  fromCity = '';
  toCountry = '';
  toState = '';
  toCity = '';
  packingDate = '';
  deliveryDate = '';
  details = '';
  resolution = '';
  agree = false;
  companyWebsite = ''; // Honeypot

  moverListVisible = false;
  moverResults: any[] = [];
  moverActiveIndex = -1;
  showMoverMissing = false;

  fromCityListVisible = false;
  fromCityResults: string[] = [];
  toCityListVisible = false;
  toCityResults: string[] = [];

  formSubmitted = false;
  clientErrors: string[] = [];
  serverErrors: any[] = [];

  countries = [];
  today = new Date().toISOString().slice(0, 10);

  cities = [];
  /*
  cities: any = {
    'United States': ['Atlanta','Boston','Chicago','Dallas','Denver','Houston', 'Los Angeles','Miami','New York','Philadelphia','Phoenix','San Francisco', 'Seattle','Washington'],
    'Canada': ['Calgary','Edmonton','Halifax','Montreal','Ottawa','Toronto', 'Vancouver','Winnipeg'],
    'Australia': ['Adelaide','Brisbane','Canberra','Darwin','Hobart','Melbourne', 'Perth','Sydney'],
    'United Kingdom': ['Belfast','Birmingham','Bristol','Cardiff','Edinburgh', 'Glasgow','Leeds','London','Manchester'],
    'United Arab Emirates': ['Abu Dhabi','Al Ain','Dubai','Fujairah','Sharjah'],
    'Singapore': ['Singapore'],
    'Philippines': ['Cebu City','Davao','Makati','Manila','Quezon City'],
    'Germany': ['Berlin','Cologne','Frankfurt','Hamburg','Munich','Stuttgart'],
    'France': ['Bordeaux','Lille','Lyon','Marseille','Nice','Paris','Toulouse'],
    'India': ['Ahmedabad','Bengaluru','Chennai','Delhi','Hyderabad','Kolkata', 'Mumbai','Pune'],
    'Netherlands': ['Amsterdam','Eindhoven','Rotterdam','The Hague','Utrecht'],
    'Japan': ['Fukuoka','Kobe','Kyoto','Nagoya','Osaka','Tokyo','Yokohama'],
    'China': ['Beijing','Chengdu','Guangzhou','Shanghai','Shenzhen'],
    'Hong Kong': ['Hong Kong','Kowloon'],
    'Brazil': ['Brasilia','Belo Horizonte','Curitiba','Rio de Janeiro','Sao Paulo'],
    'Mexico': ['Cancun','Guadalajara','Mexico City','Monterrey','Tijuana'],
    'Spain': ['Barcelona','Bilbao','Madrid','Malaga','Seville','Valencia'],
    'Italy': ['Bologna','Florence','Milan','Naples','Rome','Turin'],
    'Ireland': ['Cork','Dublin','Galway','Limerick'],
    'Switzerland': ['Basel','Bern','Geneva','Lausanne','Zurich'],
    'New Zealand': ['Auckland','Christchurch','Wellington'],
    'Belgium': ['Antwerp','Brussels','Ghent'],
    'Qatar': ['Doha'],
    'Saudi Arabia': ['Dammam','Jeddah','Mecca','Riyadh'],
    'South Africa': ['Cape Town','Durban','Johannesburg','Pretoria']
  };
  */

  constructor(private router: Router, private appService: AppService) { }

  ngOnInit(): void {
    this.makeCode();
    this.moverSearchSubscription = this.moverSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.performMoverSearch(query);
    });

    this.appService.getCountries().subscribe(response => {
      this.countries = response.body;
    }, error => {
      console.error('Error fetching countries', error);
    });
  }

  ngOnDestroy(): void {
    if (this.moverSearchSubscription) {
      this.moverSearchSubscription.unsubscribe();
    }
  }

  makeCode() {
    this.currentCode = '';
    this.codeArt = [];
    for (let i = 0; i < 6; i++) {
      const ch = this.codeChars.charAt(Math.floor(Math.random() * this.codeChars.length));
      this.currentCode += ch;
      this.codeArt.push({
        ch: ch,
        style: {
          transform: `rotate(${(Math.random() * 16 - 8).toFixed(1)}deg) translateY(${(Math.random() * 6 - 3).toFixed(1)}px)`
        }
      });
    }
  }

  verifyStage1() {
    this.stage1Errors = {};
    let ok = true;

    if (!this.email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.email)) {
      this.stage1Errors.email = 'Enter an email address in the format name@example.com';
      ok = false;
    }

    if (!this.emailConfirm) {
      this.stage1Errors.emailConfirm = 'Re-enter your email address';
      ok = false;
    } else if (this.email.toLowerCase() !== this.emailConfirm.toLowerCase()) {
      this.stage1Errors.emailConfirm = 'The two email addresses do not match';
      ok = false;
    }

    if (!this.gotcha) {
      this.stage1Errors.gotcha = 'Enter the characters shown';
      ok = false;
    } else if (this.gotcha.toUpperCase() !== this.currentCode) {
      this.stage1Errors.gotcha = 'That does not match the characters shown. Try the new code.';
      this.makeCode();
      this.gotcha = '';
      ok = false;
    }

    if (ok) {
      this.stage1Done = true;
      setTimeout(() => {
        const first = document.getElementById('first-name');
        if (first) first.focus();
      }, 100);
    }
  }

  onMoverInput() {
    this.moverId = '';
    const q = this.moverQuery.trim();
    if (q.length < 2) {
      this.moverResults = [];
      this.moverListVisible = false;
      return;
    }
    this.moverSearchSubject.next(q);
  }

  performMoverSearch(q: string) {
    this.appService.searchServiceProvidersByName(q, { size: 10 }).subscribe(response => {
      const results = response.body || [];
      this.moverResults = results.map((m: any) => ({
        id: m.id,
        name: m.companylegalname || m.name,
        meta: (m.city ? m.city + ', ' : '') + (m.country || '')
      }));
      this.moverListVisible = true;
      this.moverActiveIndex = -1;
    }, error => {
      console.error('Error searching movers', error);
      this.moverResults = [];
      this.moverListVisible = true;
    });
  }

  chooseMover(m: any) {
    this.moverQuery = m.name;
    this.moverId = m.id;
    this.moverListVisible = false;
  }

  onCityInput(type: 'from' | 'to') {
    const country = type === 'from' ? this.fromCountry : this.toCountry;
    const q = (type === 'from' ? this.fromCity : this.toCity).trim().toLowerCase();
    if (!q) {
      if (type === 'from') this.fromCityListVisible = false;
      else this.toCityListVisible = false;
      return;
    }
    const list = this.cities[country] || [];
    const results = list.filter((c: string) => c.toLowerCase().startsWith(q)).slice(0, 8);
    if (type === 'from') {
      this.fromCityResults = results;
      this.fromCityListVisible = results.length > 0;
    } else {
      this.toCityResults = results;
      this.toCityListVisible = results.length > 0;
    }
  }

  chooseCity(type: 'from' | 'to', city: string) {
    if (type === 'from') {
      this.fromCity = city;
      this.fromCityListVisible = false;
    } else {
      this.toCity = city;
      this.toCityListVisible = false;
    }
  }

  onDateTriggerClick(event: MouseEvent, input: HTMLInputElement) {
    if (input && (input as any).showPicker) {
      (input as any).showPicker();
    }
  }

  onSubmit(event: Event) {
    event.preventDefault();
    if (this.companyWebsite) {
      this.router.navigate(['/issue-filed'], { queryParams: { ref: 'CISS-BOT' } });
      return;
    }

    this.clientErrors = [];
    if (!this.firstName.trim()) this.clientErrors.push('first-name');
    if (!this.lastName.trim()) this.clientErrors.push('last-name');
    if (!this.moverId) this.clientErrors.push('mover');
    if (!this.fromCountry) this.clientErrors.push('from-country');
    if (!this.fromCity.trim()) this.clientErrors.push('from-city');
    if (!this.toCountry) this.clientErrors.push('to-country');
    if (!this.toCity.trim()) this.clientErrors.push('to-city');
    if (!this.packingDate) this.clientErrors.push('packing-date');
    if (this.deliveryDate && this.packingDate && this.deliveryDate < this.packingDate) this.clientErrors.push('delivery-date');
    if (!this.details.trim()) this.clientErrors.push('details');
    if (!this.resolution.trim()) this.clientErrors.push('resolution');
    if (!this.agree) this.clientErrors.push('agree');

    if (this.clientErrors.length > 0) {
      setTimeout(() => {
        this.errorSummary.nativeElement.focus();
        this.errorSummary.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return;
    }

    const cissData = {
      emailAddress: this.email,
      firstName: this.firstName,
      lastName: this.lastName,
      movingCompanyName: this.moverQuery,
      movedFromCountry: this.fromCountry,
      movedFromStateOrProvince: this.fromState,
      movedFromCity: this.fromCity,
      movedToCountry: this.toCountry,
      movedToStateOrProvince: this.toState,
      movedToCity: this.toCity,
      packingDate: this.packingDate,
      deliveryDate: this.deliveryDate,
      fullDetails: this.details,
      expectedOutcome: this.resolution,
      confirmation: this.agree ? 'true' : 'false'
    };

    console.log('Submitting CISS form');
    // Disable form submission until we have a backend to handle the form submission
    const ref = 'CISS-' + new Date().getFullYear() + '-' + Math.floor(Math.random() * 90000 + 10000);
    this.router.navigate(['/issue-filed'], { queryParams: { ref: ref } });
    /*
    this.appService.submitCissForm(cissData).subscribe(response => {
      // TODO: Need to discuss how we handle the response from the server and get the reference number
      const ref = 'CISS-' + new Date().getFullYear() + '-' + Math.floor(Math.random() * 90000 + 10000);
      this.router.navigate(['/issue-filed'], { queryParams: { ref: ref } });
    }, error => {
      console.error('Error submitting CISS form', error);
      this.serverErrors = error.error?.errors || ['There was an error submitting your issue. Please try again later.'];
      this.clientErrors = ['server-error'];
      setTimeout(() => {
        this.errorSummary.nativeElement.focus();
        this.errorSummary.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    });
     */
  }

  scrollToField(id: string) {
    const el = document.getElementById(id);
    if (el) {
      el.focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  getErrorMessage(id: string): string {
    const messages: any = {
      'first-name': 'Enter your first name',
      'last-name': 'Enter your last name',
      'email': 'Enter an email address in the format name@example.com',
      'mover': 'Choose your moving company from the list',
      'from-country': 'Choose the country you moved from',
      'from-city': 'Enter the city you moved from',
      'to-country': 'Choose the country you moved to',
      'to-city': 'Enter the city you moved to',
      'packing-date': 'Enter the date your belongings were packed',
      'delivery-date': 'The delivery date cannot be before the packing date',
      'details': 'Describe what happened',
      'resolution': 'Describe what outcome would settle this',
      'agree': 'Confirm you have read the process and privacy notice',
      'server-error': 'There was an error submitting your issue. Please try again later.'
    };
    return messages[id] || 'Problem with this field';
  }

  hasError(id: string): boolean {
    return this.clientErrors.includes(id);
  }
}
